"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const common_utils_1 = require("@as-react/common-utils");
const common_types_1 = require("@as-react/common-types");
class RethrownError extends Error {
    constructor(message, error) {
        super(message);
        this.name = this.constructor.name;
        if (!error)
            throw new Error('RethrownError requires a message and error');
        this.original_error = error;
        this.stack_before_rethrow = this.stack;
        const message_lines = (this.message.match(/\n/g) || []).length + 1;
        this.stack = `${(this.stack || '')
            .split('\n')
            .slice(0, message_lines + 1)
            .join('\n')}\n${error.stack}`;
    }
}
const SENTRY_DSN_KEY = {
    'ecomm-acc': 'f9cb565a4b8f88d18defb73a59f13a57',
    'ecomm-prd': 'e1a94a48697e4969a181ffc80dcdb7ed',
};
class Logger {
    init(options) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g;
            const cookieConsentSettings = (0, common_utils_1.cookieConsentSettingsParser)(decodeURIComponent((_a = common_utils_1.CookieUtil.get(common_types_1.Cookie.CONSENT_SETTING)) !== null && _a !== void 0 ? _a : ''));
            const shouldMonitorPage = ((_c = (_b = SITE.Config) === null || _b === void 0 ? void 0 : _b.pageInfo) === null || _c === void 0 ? void 0 : _c.isBasketOverviewPage) ||
                ((_e = (_d = SITE.Config) === null || _d === void 0 ? void 0 : _d.pageInfo) === null || _e === void 0 ? void 0 : _e.isBuyPage) ||
                ((_g = (_f = SITE.Config) === null || _f === void 0 ? void 0 : _f.pageInfo) === null || _g === void 0 ? void 0 : _g.isConfirmationPage);
            const canMonitorPage = cookieConsentSettings && cookieConsentSettings[common_types_1.CookieConsentGroups.ANALYTICS];
            if (shouldMonitorPage &&
                canMonitorPage &&
                (common_utils_1.AppEnvironmentUtil.isProduction() || common_utils_1.AppEnvironmentUtil.getName() === 'ecomm-acc')) {
                const Sentry = yield Promise.resolve().then(() => tslib_1.__importStar(require('@sentry/react')));
                const { ExtraErrorData } = yield Promise.resolve().then(() => tslib_1.__importStar(require('@sentry/integrations')));
                Sentry.init({
                    dsn: `https://${SENTRY_DSN_KEY[common_utils_1.AppEnvironmentUtil.getName()]}@o990758.ingest.sentry.io/5947634`,
                    integrations: [new ExtraErrorData()],
                    normalizeDepth: 3,
                    release: `product-lister-react@${options.release}`,
                    environment: common_utils_1.AppEnvironmentUtil.getName(),
                    ignoreErrors: [
                        'Saferpay-fields',
                        'MutationObserver',
                        'Hydration failed',
                        'ResizeObserver loop completed',
                        'cobrowseIOgetRedactedElementsBounds',
                    ],
                    denyUrls: [
                        /sizebay\.technology/i,
                        /reevoo\.com/i,
                        /klarna\.com/i,
                        /rs\.fullstory\.com/i,
                        /ekr\.zdassets\.com/i,
                        /stackadapt.com/i,
                        /\/(gtm|ga|analytics)\.js/i,
                        /google-analytics\.com/i,
                        /omtrdc\.net/i,
                        /omtrdc\.net/i,
                        /www\.saferpay\.com/i,
                        /graph\.facebook\.com/i,
                        /connect\.facebook\.net\/en_US\/all\.js/i,
                        /extensions\//i,
                        /^chrome:\/\//i,
                        /^chrome-extension:\/\//i,
                    ],
                    beforeSend: (event, hint) => {
                        var _a, _b;
                        const originalException = hint.originalException;
                        const statusCode = (_b = (_a = originalException === null || originalException === void 0 ? void 0 : originalException.original_error) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.status;
                        if (statusCode && !common_types_1.HTTPServerErrorCodes.includes(statusCode)) {
                            return null;
                        }
                        return event;
                    },
                });
                Sentry.setTag('pageType', SITE.Config.analyticsPageType);
                Sentry.setTag('mainWebShop', SITE.Config.mainWebShop);
                Sentry.setTag('isShopLanding', SITE.Config.isShopLanding);
                this.SentryInstance = Sentry;
                this.reduxEnhancer = Sentry.createReduxEnhancer({});
            }
        });
    }
    logError(...data) {
        var _a, _b;
        if (!this.SentryInstance) {
            return;
        }
        const [partA, partB] = data;
        (_b = (_a = this.SentryInstance).withScope) === null || _b === void 0 ? void 0 : _b.call(_a, (scope) => {
            var _a, _b;
            let errorToCapture = (0, common_utils_1.isObject)(partA) ? new Error(partA === null || partA === void 0 ? void 0 : partA.message) : new Error(data.toString());
            let failedRequestData;
            if (partA &&
                typeof partA === 'string' &&
                partB &&
                typeof partB === 'object' &&
                this.isErrorFailedRequest(partB)) {
                errorToCapture = new RethrownError(partA, partB);
                failedRequestData = this.mapFailedRequestData(partB);
            }
            else if (partA && typeof partA === 'object' && this.isErrorFailedRequest(partA)) {
                errorToCapture = new RethrownError(partA.message, partA);
                failedRequestData = this.mapFailedRequestData(partA);
            }
            if (failedRequestData) {
                scope.setExtra('failedRequest', failedRequestData);
                scope.setTag('failed_request', true);
                scope.setTag('failed_request_url', failedRequestData.requestUrl);
                scope.setTag('failed_request_method', failedRequestData.requestMethod);
                scope.setTag('failed_request_response_code', failedRequestData.responseCode);
                scope.setTag('traceId', (_a = failedRequestData.responseData) === null || _a === void 0 ? void 0 : _a.traceId);
            }
            (_b = this.SentryInstance) === null || _b === void 0 ? void 0 : _b.captureException(errorToCapture);
        });
    }
    isErrorFailedRequest(error) {
        return !!error.request && !!error.config;
    }
    mapFailedRequestData(failedRequest) {
        var _a, _b, _c;
        const mappedData = {
            requestUrl: failedRequest.config.url,
            requestMethod: failedRequest.config.method,
            requestParams: failedRequest.config.params,
            requestData: failedRequest.config.data,
            responseCode: (_a = failedRequest.response) === null || _a === void 0 ? void 0 : _a.status,
            responseText: (_b = failedRequest.response) === null || _b === void 0 ? void 0 : _b.statusText,
            responseData: (_c = failedRequest.response) === null || _c === void 0 ? void 0 : _c.data,
        };
        return mappedData;
    }
}
const loggerInstance = new Logger();
exports.default = loggerInstance;
//# sourceMappingURL=Logger.js.map